// One setting's value with its editor (S9 sections, S12 Tool settings). Values are the canonical
// text the settings registry stores (shared/constants/settings.ts): "true"/"false", digits,
// ISO 8601 durations, "HH:MM", or a choice's value. The server validates; its refusal shows inline.
import type { SettingView } from '@feedback/shared/contracts/settings';
import { useEffect, useState } from 'react';
import { humanise } from '@/lib/format';
import { describeDuration, errorText } from '../lib/admin-api';
import { Badge, Button, Input, Select, Switch } from '../ui/primitives';

export interface SettingSaver {
  save: (setting: SettingView, value: string) => Promise<unknown>;
}

const HINTS: Record<SettingView['valueType'], string> = {
  boolean: '',
  integer: 'A whole number',
  decimal: 'A number, for example 0.2',
  duration: 'ISO 8601, for example PT15S (15 seconds), PT2M, P14D (14 days)',
  time_of_day: 'HH:MM, 24-hour',
  text: '',
  choice: '',
};

/** "15 seconds", "On", "Right", "not set". */
export function displayValue(setting: Pick<SettingView, 'valueType' | 'choices'>, value: string | null): string {
  if (value === null) {
    return 'not set';
  }
  switch (setting.valueType) {
    case 'boolean':
      return value === 'true' ? 'On' : 'Off';
    case 'duration':
      return describeDuration(value);
    case 'choice':
      return setting.choices.find((c) => c.value === value)?.label ?? value;
    default:
      return value;
  }
}

export function bounds(setting: SettingView): string | null {
  const fmt = (v: string) => (setting.valueType === 'duration' ? describeDuration(v) : v);
  if (setting.minValue && setting.maxValue) {
    return `${fmt(setting.minValue)} to ${fmt(setting.maxValue)}`;
  }
  if (setting.minValue) {
    return `at least ${fmt(setting.minValue)}`;
  }
  if (setting.maxValue) {
    return `at most ${fmt(setting.maxValue)}`;
  }
  return null;
}

/** The editor alone: a switch for booleans (saves at once), otherwise a control and Save. */
export function SettingControl({
  setting,
  saver,
  label,
  disabled,
}: {
  setting: SettingView;
  saver: SettingSaver;
  /** Accessible name; defaults to the description. */
  label?: string;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState(setting.value ?? '');
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const name = label ?? setting.description;
  const readOnly = disabled || !setting.canEdit;

  // A saved (or refetched) value replaces whatever was typed.
  useEffect(() => {
    setDraft(setting.value ?? '');
  }, [setting.value]);

  async function save(value: string) {
    setBusy(true);
    setError(undefined);
    try {
      await saver.save(setting, value);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  if (setting.valueType === 'boolean') {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <Switch
            checked={setting.value === 'true'}
            onCheckedChange={(on) => save(on ? 'true' : 'false')}
            label={name}
            disabled={readOnly || busy}
          />
          <span className="text-sm">{setting.value === 'true' ? 'On' : 'Off'}</span>
        </div>
        {error ? (
          <p role="alert" className="text-xs font-medium text-danger">
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  const dirty = draft !== (setting.value ?? '');
  return (
    <form
      className="flex flex-col gap-1"
      onSubmit={(event) => {
        event.preventDefault();
        void save(draft.trim());
      }}
    >
      <div className="flex flex-wrap items-center gap-2">
        {setting.valueType === 'choice' ? (
          <Select
            aria-label={name}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={readOnly}
            className="w-auto min-w-32"
          >
            {setting.value === null ? <option value="">Not set</option> : null}
            {setting.choices.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </Select>
        ) : (
          <Input
            aria-label={name}
            aria-invalid={Boolean(error)}
            value={draft}
            placeholder={setting.defaultValue ?? 'not set'}
            onChange={(e) => setDraft(e.target.value)}
            disabled={readOnly}
            inputMode={setting.valueType === 'integer' || setting.valueType === 'decimal' ? 'decimal' : undefined}
            type={setting.valueType === 'time_of_day' ? 'time' : 'text'}
            className="w-40"
          />
        )}
        {readOnly ? null : (
          <Button
            size="sm"
            variant="primary"
            type="submit"
            aria-label={`Save ${name}`}
            busy={busy}
            disabled={!dirty || draft.trim() === ''}
          >
            Save
          </Button>
        )}
        {setting.valueType === 'duration' && setting.value ? (
          <span className="text-xs text-muted">{describeDuration(setting.value)}</span>
        ) : null}
      </div>
      {HINTS[setting.valueType] && !readOnly ? <p className="text-xs text-muted">{HINTS[setting.valueType]}</p> : null}
      {error ? (
        <p role="alert" className="text-xs font-medium text-danger">
          {error}
        </p>
      ) : null}
    </form>
  );
}

/** Where the effective value came from. */
export function SourceBadge({ setting }: { setting: SettingView }) {
  if (setting.source === 'unset') {
    return <Badge tone="warning">not set</Badge>;
  }
  if (setting.source === 'default') {
    return <Badge>default</Badge>;
  }
  return <Badge tone="info">set</Badge>;
}

export function settingTitle(code: string): string {
  const last = code.split('.').pop() ?? code;
  return humanise(last);
}
